import { assets, imageVariants, pageAt } from "../content";

/** Deployment prefix, e.g. "/" or "/XeratecWebsite/". */
export const base = import.meta.env.BASE_URL;

/**
 * Rewrite a captured URL so it works on the static build: downloaded assets and
 * xeratec.com pages become base-relative; anything else passes through.
 */
export function localHref(href = "") {
  if (assets[href]) return base + assets[href];
  if (/^https?:\/\/(www\.)?xeratec.com/.test(href)) {
    const url = new URL(href);
    if (pageAt(url.pathname))
      return base + url.pathname.replace(/^\//, "") + url.hash;
  }
  if (href.startsWith("/")) {
    const [pathname, hash] = href.split("#");
    if (pageAt(pathname))
      return base + pathname.slice(1) + (hash ? "#" + hash : "");
  }
  return href;
}

const assetsByFilename = new Map();
for (const [url, file] of Object.entries(assets)) {
  const name = url.slice(url.lastIndexOf("/") + 1);
  if (!assetsByFilename.has(name)) assetsByFilename.set(name, file);
}

/** Local URL of a downloaded asset, looked up by its original filename. */
export function assetUrl(filename) {
  const file = assetsByFilename.get(filename);
  return file ? base + file : "";
}

/**
 * `<img>` props for a local image URL: `src` plus a srcset of every captured
 * WordPress size, so a small thumbnail reference still renders sharply.
 * `sizes` is the image's rendered width (CSS lengths / media conditions).
 */
export function imageProps(src, sizes) {
  const set = imageVariants.sets[imageVariants.files[src?.slice(base.length)]];
  if (!set) return { src };
  const srcSet = set.map(([file, width]) => `${base}${file} ${width}w`).join();
  return { src, srcSet, sizes };
}

/**
 * `<img>` props for a photo given an original asset filename (looked up
 * locally, with srcset), a path under public/images/, or a resolved URL.
 */
export function photoProps(nameOrUrl, sizes) {
  if (/^(\/|data:|https?:)/.test(nameOrUrl)) return { src: nameOrUrl };
  // A client logo from the home page's strip, e.g. "brand/clients/x.png".
  if (nameOrUrl.startsWith("brand/")) return { src: `${base}${nameOrUrl}` };
  const captured = assetUrl(nameOrUrl);
  // Not from the old site: a photo added under public/images/, e.g.
  // "team/jane-doe.jpg".
  if (!captured) return { src: `${base}images/${nameOrUrl}` };
  return imageProps(captured, sizes);
}

/** Normalized route path for a browser pathname: base stripped, trailing slash. */
export function routePath(pathname) {
  let path = decodeURI(pathname);
  if (base !== "/" && path.startsWith(base))
    path = "/" + path.slice(base.length);
  return path.endsWith("/") ? path : path + "/";
}

/** Pathname of an absolute or site-relative URL. */
export const pathnameOf = (href) =>
  new URL(href, "https://xeratec.com").pathname;
